// src/lib/voiceConsent.js
//
// Ticket 6b, section 9.9 — stored voice notes: the client's half of consent.
//
// A READER IS ASKED FOR VOICE CONSENT ONCE, EVER. Decided, and not
// negotiable: any voice_consents row for this reader, at any version, means
// they have agreed and the modal never shows again -- not after a wording
// change, not after a version bump, not on another device.
//
// It used to be the opposite. The read below filtered on the current
// CONSENT_VERSION, so a version bump was designed to re-ask everybody. Then
// #167 bumped this file to v2 and left both edge functions writing and
// checking v1, so every reader who agreed once was asked again on every mic
// press, and agreeing failed on the (user_id, consent_version) primary key:
// "Could not save your consent". Filtering on the version is what made a
// second ask possible at all, so nothing filters on it any more.
// npm run check:consent-version fails the build if a consent read filters
// on consent_version again.
//
// CONSENT_VERSION still exists, and still has to match the edge function's
// copy (voice-consent/index.ts, which is what actually gets written): it is
// the label for WHICH text a reader agreed to, stored on their one row as
// evidence. It never decides whether to ask. The same check fails the build
// if the two copies differ.
//
// CONSENT_TEXT below is the lawyer-approved wording (received 2026-09-12),
// not a paraphrase of it -- it is the reader's own agreement, verbatim, so
// nobody should reword, shorten, or "clean up" any part of it without going
// back through legal first, same rule that applied while this was still a
// placeholder. It satisfies what 9.9 requires in one string: what is
// captured, that it is used solely for playback (and explicitly never for
// training), the destruction schedule, and a mention of the Voice &
// Biometric Data Policy. ProfileBlocks.jsx locates that exact phrase inside
// this string and wraps just that substring in the required link
// (reanloforreaders.com/privacy#voice-biometric-data, per #166) rather than
// this file and that one each hand-typing the sentence and risking the two
// copies drifting apart -- see that file's own comment on the split.
//
// CONSENT_VERSION moved from '2026-09-v1' to '2026-09-v2' when the
// placeholder wording was replaced by the lawyer's (#167): one real reader
// had ticked the placeholder, and a version string exists so a stored row
// says which text it attests to. That is its only job. Per the rule at the
// top of this file, a reader who holds a row at ANY version is never asked
// again -- including that one.
export const CONSENT_VERSION = '2026-09-v2';

export const CONSENT_TEXT = `I agree that Reanlo can record, store & play back my voice for my journal entries. My recordings are used only to play my notes back to me, are never used to train AI, and are permanently deleted when I delete the note, delete my account, or after 3 years of inactivity. I've read the Voice & Biometric Data Policy.`;

import { supabase } from './supabase';
import { getCurrentUser } from './auth';

// Once this tab has seen a grant for a reader, it does not ask the database
// again for that reader. Keyed on the user id, so a different account signing
// in on the same tab is still checked and still asked.
let knownGrantedFor = null;

// True if this reader has EVER agreed -- a row at any consent_version.
// Deliberately NOT filtered on CONSENT_VERSION: see the rule at the top of
// this file. .limit(1) rather than .maybeSingle(): the table is keyed on
// (user_id, consent_version), so one reader can hold more than one row, and
// .maybeSingle() throws on two.
//
// Throws on a failed read. The caller must treat that as "could not check",
// never as "not granted": showing the modal to a reader who already agreed
// is exactly the second ask this file exists to prevent.
export async function hasGrantedVoiceConsent() {
  const user = await getCurrentUser();
  if (!user) return false;
  if (knownGrantedFor === user.id) return true;

  const { data, error } = await supabase
    .from('voice_consents')
    .select('user_id')
    .eq('user_id', user.id)
    .limit(1);
  if (error) throw error;
  const granted = (data || []).length > 0;
  if (granted) knownGrantedFor = user.id;
  return granted;
}

// Records a grant. Must only be called after the reader has actually seen
// CONSENT_TEXT and ticked the (unticked-by-default) checkbox -- this
// function itself has no way to verify that happened and trusts its
// caller completely, same as the upload flow trusts that THIS ran first.
// IP address is captured server-side by the edge function, not passed
// from here -- see that function's own comment on why.
//
// Called only when hasGrantedVoiceConsent() has returned false. The edge
// function is idempotent -- a reader who already holds a row gets "ok" back
// rather than a primary-key error -- so a double click, or two tabs, cannot
// turn an agreement into "Could not save your consent".
export async function grantVoiceConsent() {
  const { data, error: invokeError } = await supabase.functions.invoke('voice-consent', {
    body: {},
  });
  if (invokeError) {
    let message = invokeError.message;
    try {
      const body = await invokeError.context?.json?.();
      if (body?.error) message = body.error;
    } catch {
      // context wasn't JSON or already consumed -- fall back to
      // invokeError.message rather than masking it with a second error.
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.error);
  const user = await getCurrentUser();
  if (user) knownGrantedFor = user.id;
  return data;
}
